import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { dt } from '../assets/styles/theme';

const MONO = "'IBM Plex Mono', monospace";
const CHAR_INTERVAL_MS = 16;
const HOLD_MS = 2200;
const HOLD_WITH_UNDO_MS = 4500;

// Streams the head of `queue` into the header bar one character at a time, then holds it
// before advancing — replaces the old floating toasts/snackbars that used to sit on top of
// the canvas and side panel.
const HeaderNotifications = ({ queue, onDismiss, onUndo }) => {
  const current = queue[0] || null;
  const [shown, setShown] = useState(0);
  const [cursorOn, setCursorOn] = useState(true);
  const streamTimerRef = useRef(null);
  const holdTimerRef = useRef(null);

  useEffect(() => {
    setShown(0);
    if (streamTimerRef.current) clearInterval(streamTimerRef.current);
    if (holdTimerRef.current) clearTimeout(holdTimerRef.current);
    if (!current) return undefined;

    streamTimerRef.current = setInterval(() => {
      setShown((n) => {
        const next = n + 1;
        if (next >= current.text.length) {
          clearInterval(streamTimerRef.current);
          streamTimerRef.current = null;
          holdTimerRef.current = setTimeout(() => {
            onDismiss(current.id);
          }, current.undo ? HOLD_WITH_UNDO_MS : HOLD_MS);
        }
        return next;
      });
    }, CHAR_INTERVAL_MS);

    return () => {
      if (streamTimerRef.current) clearInterval(streamTimerRef.current);
      if (holdTimerRef.current) clearTimeout(holdTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.id]);

  useEffect(() => {
    const t = setInterval(() => setCursorOn((c) => !c), 500);
    return () => clearInterval(t);
  }, []);

  if (!current) return null;

  const text = current.text.slice(0, shown);
  const isDone = shown >= current.text.length;
  const color = current.tone === 'error' ? '#f4716a' : dt.amber;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
      <Box
        component="span"
        sx={{
          fontFamily: MONO,
          fontSize: '12.5px',
          letterSpacing: '.02em',
          color,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: '360px',
        }}
      >
        {text}
        <Box component="span" sx={{ opacity: cursorOn ? 1 : 0, color, ml: '2px' }}>▋</Box>
      </Box>
      {isDone && current.undo && (
        <Box
          component="button"
          onClick={() => onUndo(current.id)}
          sx={{
            background: 'transparent',
            border: 'none',
            color: dt.amberHover,
            fontSize: '11.5px',
            fontWeight: 500,
            cursor: 'pointer',
            textDecoration: 'underline',
            p: 0,
            flexShrink: 0,
            fontFamily: "'Lexend', sans-serif",
          }}
        >
          Undo
        </Box>
      )}
    </Box>
  );
};

export default HeaderNotifications;
